import { useServicesContext } from "@/modules/core/services/services.context"
import {
  MemberSessionHistoryItemEntity,
  MemberSessionHistorySummaryEntity,
} from "@/modules/members/entities/memberSessionHistoryEntity"
import type { SESSION_HISTORY_STATUS_FILTER } from "@/modules/members/gateway/memberApiGateway"
import { memberSessionHistoryKeys } from "@/modules/members/gateway/memberSessionHistoryKeys"
import {
  createSessionHistoryItemModel,
  createSessionHistorySummaryModel,
  type MemberSessionHistoryItemModel,
  type SessionHistorySummaryModel,
} from "@/modules/members/models/memberSessionHistoryModel"
import { useSuspenseInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"

const SESSION_LIST_PAGE_SIZE = 20

export const useMemberSessionHistorySummary = (memberId: string, year: number, month: number | null) => {
  const { memberApi } = useServicesContext()

  const { data: summaryDto } = useSuspenseQuery({
    queryKey: memberSessionHistoryKeys.summary({ memberId, year, month }),
    queryFn: () => memberApi.getMemberSessionHistorySummary(memberId, year, month),
  })

  const summary: SessionHistorySummaryModel = useMemo(() => {
    return createSessionHistorySummaryModel(MemberSessionHistorySummaryEntity.fromDto(summaryDto))
  }, [summaryDto])

  return { summary }
}

export const useMemberSessionHistoryList = (
  memberId: string,
  year: number,
  month: number | null,
  statusFilter: SESSION_HISTORY_STATUS_FILTER,
) => {
  const { memberApi } = useServicesContext()

  const {
    data: listData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSuspenseInfiniteQuery({
    queryKey: memberSessionHistoryKeys.list({ memberId, year, month, statusFilter }),
    queryFn: async ({ pageParam = 0 }) => {
      const dto = await memberApi.getMemberSessionHistoryList(
        memberId,
        year,
        month,
        statusFilter,
        pageParam,
        SESSION_LIST_PAGE_SIZE,
      )
      return {
        sessions: dto.content.map((item) => MemberSessionHistoryItemEntity.fromDto(item)),
        hasMore: !dto.last,
        nextPage: dto.page + 1,
      }
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
    initialPageParam: 0,
  })

  const sessions: MemberSessionHistoryItemModel[] = useMemo(
    () => listData.pages.flatMap((page) => page.sessions.map(createSessionHistoryItemModel)) ?? [],
    [listData.pages],
  )

  return { sessions, fetchNextPage, hasNextPage, isFetchingNextPage }
}
